import React, { useState } from "react";
import { lvService } from "../../services/LvService";

import { Box, CircularProgress, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormGroup from "@mui/material/FormGroup";
import EditIcon from "@mui/icons-material/Edit";

import { SelectInput, required } from "react-admin";
import { useFormContext } from "react-hook-form";

const useStyles = (theme) => ({
    paper: {
        position: "absolute",
        width: 400,
        backgroundColor: theme.palette.background.paper,
        border: "2px solid #000",
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
    },
    modalContainer: {
        top: `50%`,
        left: `50%`,
        transform: `translate(-50%, -50%)`,
        width: "50%",
    },
    loaderContainer: {
        display: "flex",
        justifyContent: "center",
    },
});

export default function ChartConfigEditor({ getEntityIds }) {
    const theme = useTheme();
    const classes = useStyles(theme);

    const [open, setOpen] = useState(false);
    const [chartId, setChartId] = useState(null);
    const [chartType, setChartType] = useState("complex");
    const [allClientCharts, setClientCharts] = useState([]);
    const [assignedCharts, setAssignedCharts] = useState([]);
    const [loading, setLoading] = useState(false);

    const { setValue } = useFormContext();

    const validEntityIds = () => {
        const entities = getEntityIds();
        const entityIds = entities.siteIds || entities.pathIds || entities.lineIds;

        if (Object.keys(entities).length != 1) {
            alert("Error: Must have exactly one entity type");
            return false;
        }
        if (!entityIds || entityIds.length === 0) {
            alert("Error: No entities selected");
            return false;
        }
        return true;
    };

    const handleOpen = async () => {
        if (!validEntityIds()) {
            return false;
        }

        setLoading(true);
        setOpen(true);

        await Promise.all([
            lvService.getClientCharts({ sort: '["name","ASC"]' }),
            lvService.getAssignedClientCharts(getEntityIds()),
        ])
            .then(([clientCharts, assignedCharts]) => {
                setAssignedCharts(assignedCharts);

                if (clientCharts?.count > 0) {
                    setClientCharts(clientCharts);
                }
            })
            .catch((error) => {
                console.error(`Error in promises ${error}`);
            });

        setLoading(false);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const getCurrentChart = (type) => {
        let chartIds = new Set(Object.values(assignedCharts[type] ? assignedCharts[type] : {}));

        if (chartIds.size === 0) {
            return "";
        }
        if (chartIds.size === 1) {
            return chartIds.values().next().value;
        }

        return "multiple";
    };

    const clientChartChoices = () => {
        if (getCurrentChart(chartType) === "multiple") {
            // Need to use concat so that we don't modify allClientCharts
            return allClientCharts.rows.concat([
                {
                    id: "multiple",
                    name: `Multiple Currently Assigned [${Array.from(
                        new Set(Object.values(assignedCharts[chartType]))
                    )}]`,
                    disabled: true,
                },
            ]);
        }
        return allClientCharts.rows;
    };

    const renderContent = () => {
        if (loading) {
            return (
                <Box component="div" sx={classes.loaderContainer}>
                    <CircularProgress />
                </Box>
            );
        } else if (allClientCharts?.count > 0) {
            return (
                <FormGroup>
                    <SelectInput
                        source="chartType"
                        choices={[
                            { id: "complex", name: "Complex" },
                            { id: "simple", name: "Simple" },
                        ]}
                        initialValue={chartType}
                        onChange={(e) => {
                            setChartType(e.target.value);
                            setValue("clientCharts", getCurrentChart(e.target.value), { shouldDirty: true });
                        }}
                        validate={[required()]}
                    />

                    <SelectInput
                        source="clientCharts"
                        choices={clientChartChoices()}
                        onChange={(e) => setChartId(e.target.value)}
                        validate={[required()]}
                        initialValue={getCurrentChart(chartType)}
                    />
                </FormGroup>
            );
        } else {
            return <div>Did not fetch ClientCharts.</div>;
        }
    };

    const handleSubmit = () => {
        if (chartId === null) {
            return "Error: chartId must not be null"; // How to bring this up to the user?
        }

        let entityIds = getEntityIds();

        lvService.assignClientCharts(chartId, { entityIds, chartType });

        handleClose();
    };

    return (
        <div>
            <EditIcon onClick={handleOpen} />

            <Box component="div" sx={classes.modalContainer}>
                <Dialog fullScreen={false} open={open} onClose={handleClose} aria-labelledby="responsive-dialog-title">
                    <DialogTitle id="responsive-dialog-title">{"Select Chart to Assign"}</DialogTitle>

                    <DialogContent>
                        <DialogContentText component={"span"}>{renderContent()}</DialogContentText>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={handleClose} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleSubmit} color="primary" autoFocus>
                            Assign
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </div>
    );
}
